import React, { useState } from 'react';
import { Calendar as CalendarIcon, Send, User, Building2, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { es } from 'date-fns/locale';
import { 
  InteractiveChatForm, 
  ChatFormOption 
} from './InteractiveChatForm';

export interface DynamicIntakeFormProps {
  type: "text" | "date" | "select";
  label: string;
  field: string;
  options?: string[];
  onSubmit: (field: string, value: string) => void;
}

export const DynamicIntakeForm: React.FC<DynamicIntakeFormProps> = ({
  type,
  label,
  field,
  options,
  onSubmit
}) => {
  const [textValue, setTextValue] = useState("");
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textValue.trim()) {
      onSubmit(field, textValue);
    }
  };

  const handleDateSubmit = () => {
    if (dateValue) {
      onSubmit(field, format(dateValue, 'yyyy-MM-dd'));
    }
  };

  const handleSelectSubmit = (values: string[]) => {
    if (values.length > 0) {
      onSubmit(field, values[0]);
    }
  };

  return (
    <div className="my-6 animate-in slide-in-from-bottom-4 fade-in duration-500 max-w-lg">
      <div className="bg-white rounded-2xl border-2 border-blue-100 shadow-2xl p-6 shadow-blue-900/10 relative overflow-hidden group">
        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50/50 rounded-bl-full -mr-8 -mt-8 group-hover:bg-blue-100/50 transition-colors" />
        
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-600 rounded-lg text-white shadow-lg shadow-blue-200">
            <Building2 className="h-4 w-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest block mb-0.5">
              Requerimiento de Co-piloto
            </span>
            <h3 className="text-sm font-bold text-slate-800 leading-none">{label}</h3>
          </div>
        </div>

        {type === "text" && (
          <form onSubmit={handleTextSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dynamic-input" className="text-xs font-bold text-slate-500">
                Ingresa el valor solicitado para continuar
              </Label>
              <div className="relative">
                <Input
                  id="dynamic-input"
                  placeholder="Escribe aquí..."
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  className="h-12 border-slate-200 rounded-xl focus-visible:ring-blue-500 focus-visible:border-blue-500 bg-slate-50/30"
                  autoFocus
                />
                <Button 
                  type="submit"
                  size="icon"
                  disabled={!textValue.trim()}
                  className="absolute right-1 top-1 h-10 w-10 rounded-lg bg-blue-600 hover:bg-blue-700 shadow-sm"
                >
                  <Send className="h-4 w-4 text-white" />
                </Button>
              </div>
            </div>
          </form>
        )}

        {type === "date" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Seleccionar Fecha
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full h-12 justify-start text-left font-semibold rounded-xl border-slate-200 bg-slate-50/30",
                      !dateValue && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-3 h-4 w-4 text-blue-600" />
                    {dateValue ? format(dateValue, "PPP", { locale: es }) : "Elegir una fecha"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-2xl border-slate-200 shadow-2xl" align="start">
                  <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={setDateValue}
                    initialFocus
                    locale={es}
                    className="p-3"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button 
              onClick={handleDateSubmit}
              disabled={!dateValue}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl gap-2 shadow-lg shadow-blue-200 transition-all"
            >
              Confirmar Fecha
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {type === "select" && options && (
          <div className="-mx-6 -mb-6 mt-2 border-t border-slate-100">
            <InteractiveChatForm 
              options={options}
              multiple={false}
              onSubmit={handleSelectSubmit}
              title={label}
            />
          </div>
        )}

        {/* Footer info */}
        {type !== "select" && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-medium">
            <div className="flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Este dato es necesario para el borrador legal
            </div>
            <div className="font-bold text-blue-500 animate-pulse">
              PENDIENTE
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
